// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { getFilenameFromContentDisposition } from '@/file/utilities/get-filename-from-content-disposition';

/**
 * Saves a blob response to disk under the server's filename, rather than opening it
 * in a new tab.
 *
 * Throws when the payload is not a Blob — an endpoint that returned JSON or an error
 * page would otherwise reach createObjectURL and fail with an opaque TypeError that
 * does not name the caller.
 */
export function saveBlobResponse(
  data: unknown,
  contentDisposition: unknown,
  fallbackFilename: string
): void {
  if (!(data instanceof Blob)) {
    throw new Error('Expected a file blob response');
  }

  // Axios surfaces a repeated header as an array, which names no single file.
  const filename =
    (typeof contentDisposition === 'string'
      ? getFilenameFromContentDisposition(contentDisposition)
      : null) ?? fallbackFilename;

  const url = window.URL.createObjectURL(data);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Revoke on a later task. Revoking in the same task as the click can cancel the
  // download in WebKit, and would do so in every browser if an await were ever added
  // between the click and this line — the failure being a silent zero-byte file.
  setTimeout(() => {
    window.URL.revokeObjectURL(url);
  }, 0);
}
